import { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

/**
 * Custom hook to fetch and manage user credits
 * Fetches from GET /api/credits/me
 * 
 * Returns:
 * - credits: { credits_remaining, credits_total_granted, credits_used, user_id }
 * - loading: boolean
 * - error: string | null
 * - refetch: function to manually refresh credits
 */
export const useCredits = () => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCredits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosClient.get('/credits/me');
      
      if (response.data.ok) {
        setCredits({
          credits_remaining: response.data.credits_remaining || 0,
          credits_total_granted: response.data.credits_total_granted || 0,
          credits_used: response.data.credits_used || 0,
          user_id: response.data.user_id
        });
      } else {
        throw new Error('Failed to fetch credits');
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch credits');
      // Set default values on error
      setCredits({
        credits_remaining: 0,
        credits_total_granted: 0,
        credits_used: 0,
        user_id: null
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  return {
    credits,
    loading,
    error,
    refetch: fetchCredits
  };
};

